import { useTimeMachine } from "@/contexts/TimeMachineContext"
import { useAttendanceEvents } from "@/entities/attendanceEvent/useAttendanceEvent"
import { addDays, eachDayOfInterval, endOfDay, format, startOfDay, startOfWeek } from "date-fns"
import { fr } from "date-fns/locale"
import { Copy } from "lucide-react"
import { useParams } from "react-router-dom"

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"]

export default function DebugAttendances() {
   const { now: today } = useTimeMachine()
   const { young_id } = useParams<{ young_id: string }>()
   const { data: events } = useAttendanceEvents({ young_id: young_id! })

   if (!events) return <div>Loading...</div>

   const weekStart = startOfWeek(today, { weekStartsOn: 1 })
   const weekDays = eachDayOfInterval({
      start: weekStart,
      end: addDays(weekStart, 4),
   })

   const handleCopy = (id: string) => {
      navigator.clipboard.writeText(id)
   }

   return (
      <div className="p-4">
         <h1 className="text-2xl font-bold mb-4">Calendrier de présence</h1>
         <div className="relative">
            {/* Heures */}
            <div className="absolute left-20 right-0 top-0 bottom-0">
               {HOURS.map((hour) => (
                  <div key={hour} className="border-t border-gray-200" style={{ height: "60px" }}>
                     <div className="absolute translate-y-6 -left-16 text-sm text-gray-500">
                        {hour}:00
                     </div>
                  </div>
               ))}
            </div>

            {/* Jours */}
            <div className="grid grid-cols-5 gap-2 ml-20">
               {weekDays.map((day, index) => (
                  <div key={day.toISOString()} className="relative">
                     <div className="text-center font-semibold mb-2">
                        {DAYS[index]} {format(day, "dd/MM", { locale: fr })}
                     </div>
                     <div
                        className="relative bg-gray-50 border border-gray-200"
                        style={{ height: `${24 * 60}px` }}
                     >
                        {events
                           .filter((event) => {
                              const eventDay = format(event.from, "yyyy-MM-dd")
                              const currentDay = format(day, "yyyy-MM-dd")
                              const eventEndDay = format(event.to, "yyyy-MM-dd")

                              return eventDay <= currentDay && eventEndDay >= currentDay
                           })
                           .sort((a, b) => a.from.getTime() - b.from.getTime())
                           .map((event) => {
                              const currentDayStart = startOfDay(day)
                              const currentDayEnd = endOfDay(day)

                              const eventStart =
                                 event.from < currentDayStart ? currentDayStart : event.from
                              const eventEnd = event.to > currentDayEnd ? currentDayEnd : event.to

                              const fromMinutes =
                                 eventStart.getHours() * 60 + eventStart.getMinutes()
                              const toMinutes = eventEnd.getHours() * 60 + eventEnd.getMinutes()
                              const duration = toMinutes - fromMinutes

                              return (
                                 <div
                                    key={event.id}
                                    className={`absolute left-0 right-0 mx-1 rounded px-1 text-xs border ${
                                       event.type === "present"
                                          ? "bg-green-200 border-green-500"
                                          : event.type === "late"
                                            ? "bg-yellow-200 border-yellow-500"
                                            : "bg-red-200 border-red-500"
                                    }`}
                                    style={{
                                       top: `${fromMinutes}px`,
                                       height: `${duration}px`,
                                    }}
                                    title={`${event.type} - ${format(
                                       event.from,
                                       "HH:mm",
                                    )} à ${format(event.to, "HH:mm")}`}
                                 >
                                    <div className="flex items-center justify-between">
                                       <span>{event.type}</span>
                                       <button
                                          onClick={() => handleCopy(event.id)}
                                          className="p-0.5 hover:bg-black/5 rounded"
                                          title="Copier l'ID"
                                       >
                                          <Copy size={12} />
                                       </button>
                                    </div>
                                    <div className="text-[10px] opacity-50 truncate">
                                       {event.id}
                                    </div>
                                 </div>
                              )
                           })}
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
   )
}
